// src/pages/Team.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHero } from '@/components/PageHero';
import {
  team,
  teamStats,
  getFeaturedTeam,
  type TeamMember,
  type TeamCategory,
  type SportSpecialty
} from '@/data/mock/team';
import {
  Users,
  Award,
  Target,
  TrendingUp,
  Mail,
  Phone,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Trophy,
  GraduationCap,
  Star,
  Search,
  Filter,
  X,
  ChevronRight,
  Sparkles,
  Globe,
  Calendar,
  BookOpen,
  Quote,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const Team = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // State
  const [selectedCategory, setSelectedCategory] = useState<TeamCategory | 'all'>('all');
  const [selectedSport, setSelectedSport] = useState<SportSpecialty | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  // Categories with icons
  const categories = [
    { 
      value: 'all' as const, 
      label: { en: 'All Team', ar: 'الفريق بالكامل' },
      icon: Users,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      value: 'leadership' as const, 
      label: { en: 'Leadership', ar: 'القيادة' },
      icon: Award,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      value: 'coach' as const, 
      label: { en: 'Coaches', ar: 'المدربون' },
      icon: Trophy,
      color: 'from-green-500 to-emerald-500'
    },
    { 
      value: 'medical' as const, 
      label: { en: 'Medical', ar: 'الطاقم الطبي' },
      icon: Target,
      color: 'from-red-500 to-rose-500'
    },
    { 
      value: 'staff' as const, 
      label: { en: 'Staff', ar: 'الموظفون' },
      icon: Users,
      color: 'from-orange-500 to-amber-500'
    },
  ];

  // Sports filter
  const sports = [
    { value: 'all', label: { en: 'All Sports', ar: 'جميع الرياضات' } },
    { value: 'football', label: { en: 'Football', ar: 'كرة القدم' } },
    { value: 'basketball', label: { en: 'Basketball', ar: 'كرة السلة' } },
    { value: 'swimming', label: { en: 'Swimming', ar: 'السباحة' } },
    { value: 'karate', label: { en: 'Karate', ar: 'الكاراتيه' } },
    { value: 'tennis', label: { en: 'Tennis', ar: 'التنس' } },
  ];

  // Filter team
  const filteredTeam = useMemo(() => {
    let result = team;

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(m => m.category === selectedCategory);
    }

    // Sport filter
    if (selectedSport !== 'all') {
      result = result.filter(m => 
        m.specialty?.includes(selectedSport as SportSpecialty)
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.name.en.toLowerCase().includes(query) ||
        m.name.ar.includes(searchQuery) ||
        m.role.en.toLowerCase().includes(query) ||
        m.role.ar.includes(searchQuery)
      );
    }

    // Featured filter
    if (showFeaturedOnly) {
      result = result.filter(m => m.featured === true);
    }

    return result;
  }, [selectedCategory, selectedSport, searchQuery, showFeaturedOnly]);

  // Statistics
  const stats = [
    {
      icon: Users,
      value: teamStats.totalMembers,
      label: { en: 'Team Members', ar: 'أعضاء الفريق' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Trophy,
      value: teamStats.coaches,
      label: { en: 'Expert Coaches', ar: 'مدربون خبراء' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Calendar,
      value: `${teamStats.averageExperience}+`,
      label: { en: 'Avg. Experience (Years)', ar: 'متوسط الخبرة (سنوات)' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Award,
      value: teamStats.certifications,
      label: { en: 'Certifications', ar: 'شهادات معتمدة' },
      color: 'from-orange-500 to-amber-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <PageHero
        title={isRTL ? 'فريقنا' : 'Our Team'}
        subtitle={isRTL
          ? 'تعرّف على الخبراء الذين يقودون رحلة النجاح في أكاديمية تشامبيونز'
          : 'Meet the experts leading the journey of success at Champions Academy'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.team') }
        ]}
      />

      {/* Statistics */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 shadow-xl text-center transition-all"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-3`}>
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="text-3xl font-bold gradient-text mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Team */}
      {getFeaturedTeam().length > 0 && (
        <section className="py-16 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'قادة متميزون' : 'Featured Leaders'}
                </span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 gradient-text">
                {isRTL ? 'قادة الأكاديمية' : 'Academy Leaders'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {getFeaturedTeam().slice(0, 6).map((member, idx) => (
                <FeaturedTeamCard
                  key={member.id}
                  member={member}
                  isRTL={isRTL}
                  delay={idx * 0.1}
                  onClick={() => setSelectedMember(member)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'الفريق الكامل' : 'Complete Team'}
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'تصفح فريقنا' : 'Browse Our Team'}
            </h2>
          </motion.div>

          {/* Category Filters */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap max-w-4xl mx-auto">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.value;
              
              return (
                <motion.button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      : 'bg-card border-2 border-border hover:border-primary/50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{category.label[isRTL ? 'ar' : 'en']}</span>
                  {isActive && (
                    <Badge variant="secondary" className="text-xs">
                      {filteredTeam.length}
                    </Badge>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Search & Additional Filters */}
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? 'ابحث عن عضو في الفريق...' : 'Search team member...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} h-14 text-lg`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-muted-foreground hover:text-foreground`}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Advanced Filters */}
            <div className="flex gap-4 flex-wrap items-center justify-between">
              <div className="flex gap-4 flex-wrap">
                {/* Sport Filter */}
                {selectedCategory === 'coach' || selectedCategory === 'all' && (
                  <Select
                    value={selectedSport}
                    onValueChange={(value) => setSelectedSport(value as SportSpecialty | 'all')}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sports.map(sport => (
                        <SelectItem key={sport.value} value={sport.value}>
                          {sport.label[isRTL ? 'ar' : 'en']}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Featured Toggle */}
                <Button
                  variant={showFeaturedOnly ? 'default' : 'outline'}
                  onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
                  className="gap-2"
                >
                  <Star className={`h-4 w-4 ${showFeaturedOnly ? 'fill-current' : ''}`} />
                  {isRTL ? 'المميزون فقط' : 'Featured Only'}
                </Button>
              </div>

              {/* Results Count */}
              <div className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredTeam.length} من ${team.length} عضو`
                  : `Showing ${filteredTeam.length} of ${team.length} members`
                }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            {filteredTeam.length > 0 ? (
              <motion.div
                key={`${selectedCategory}-${selectedSport}-${searchQuery}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredTeam.map((member, idx) => (
                  <TeamCard
                    key={member.id}
                    member={member}
                    isRTL={isRTL}
                    delay={idx * 0.05}
                    onClick={() => setSelectedMember(member)}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-2xl font-bold mb-2">
                  {isRTL ? 'لم يتم العثور على نتائج' : 'No Results Found'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isRTL 
                    ? 'جرّب تعديل الفلاتر أو البحث'
                    : 'Try adjusting your filters or search'
                  }
                </p>
                <Button onClick={() => {
                  setSelectedCategory('all');
                  setSelectedSport('all');
                  setSearchQuery('');
                  setShowFeaturedOnly(false);
                }}>
                  {isRTL ? 'إعادة تعيين الفلاتر' : 'Reset Filters'}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Member Detail Modal */}
      <TeamMemberModal
        member={selectedMember}
        isRTL={isRTL}
        onClose={() => setSelectedMember(null)}
      />
    </div>
  );
};

// ===== TEAM CARD COMPONENT =====
const TeamCard = ({ 
  member, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  member: TeamMember; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -8 }}
      onClick={onClick}
      className="bg-card border-2 border-border rounded-2xl overflow-hidden hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all cursor-pointer group relative"
    >
      {/* Featured Badge */}
      {member.featured && (
        <div className="absolute top-3 right-3 z-10">
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500 text-white text-xs font-bold">
            <Star className="h-3 w-3 fill-current" />
          </div>
        </div>
      )}

      {/* Image */}
      <div className="aspect-square bg-muted overflow-hidden relative">
        <img
          src={member.image}
          alt={member.name[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
          <div className="text-white">
            <p className="text-sm font-semibold mb-1">
              {isRTL ? 'اضغط للتفاصيل' : 'Click for details'}
            </p>
            {member.experience && (
              <p className="text-xs opacity-90">
                {member.experience} {isRTL ? 'سنوات خبرة' : 'years experience'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-bold mb-1 group-hover:text-primary transition-colors">
          {member.name[isRTL ? 'ar' : 'en']}
        </h3>
        <p className="text-primary text-sm font-medium mb-3">
          {member.role[isRTL ? 'ar' : 'en']}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2 mb-4">
          {member.bio[isRTL ? 'ar' : 'en']}
        </p>

        {/* Stats for coaches */}
        {member.stats && (
          <div className="grid grid-cols-2 gap-2 pt-4 border-t border-border">
            {member.stats.playersCoached && (
              <div className="text-center">
                <div className="text-lg font-bold text-primary">{member.stats.playersCoached}+</div>
                <div className="text-xs text-muted-foreground">
                  {isRTL ? 'لاعب' : 'Players'}
                </div>
              </div>
            )}
            {member.stats.championships && (
              <div className="text-center">
                <div className="text-lg font-bold text-primary">{member.stats.championships}</div>
                <div className="text-xs text-muted-foreground">
                  {isRTL ? 'بطولات' : 'Championships'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Social Links */}
        {member.social && Object.keys(member.social).length > 0 && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-border">
            {member.social.facebook && (
              <a
                href={member.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
            )}
            {member.social.instagram && (
              <a
                href={member.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {member.social.linkedin && (
              <a
                href={member.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ===== FEATURED TEAM CARD =====
const FeaturedTeamCard = ({ 
  member, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  member: TeamMember; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -10, scale: 1.02 }}
      onClick={onClick}
      className="bg-gradient-to-br from-card via-card to-primary/5 border-2 border-primary/30 rounded-3xl overflow-hidden shadow-2xl hover:shadow-primary/20 transition-all cursor-pointer group"
    >
      {/* Image */}
      <div className="aspect-square bg-muted overflow-hidden relative">
        <img
          src={member.image}
          alt={member.name[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Featured Badge */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500 text-white text-xs font-bold">
            <Star className="h-3 w-3 fill-current" />
            {isRTL ? 'مميز' : 'FEATURED'}
          </div>
        </div>

        {/* Name Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h3 className="text-2xl font-bold mb-1">{member.name[isRTL ? 'ar' : 'en']}</h3>
          <p className="text-sm opacity-90">{member.role[isRTL ? 'ar' : 'en']}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Quote or Bio */}
        {member.quote ? (
          <div className="mb-4">
            <Quote className="h-6 w-6 text-primary mb-2 opacity-50" />
            <p className="text-sm italic text-muted-foreground line-clamp-3">
              "{member.quote[isRTL ? 'ar' : 'en']}"
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
            {member.bio[isRTL ? 'ar' : 'en']}
          </p>
        )}

        {/* Highlights */}
        <div className="space-y-2 mb-4">
          {member.experience && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">
                {member.experience} {isRTL ? 'سنوات خبرة' : 'years experience'}
              </span>
            </div>
          )}
          {member.education && (
            <div className="flex items-center gap-2 text-sm">
              <GraduationCap className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground line-clamp-1">
                {member.education[isRTL ? 'ar' : 'en']}
              </span>
            </div>
          )}
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          size="sm"
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
        >
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
          <ChevronRight className={`h-4 w-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
        </Button>
      </div>
    </motion.div>
  );
};

// ===== TEAM MEMBER MODAL =====
const TeamMemberModal = ({
  member,
  isRTL,
  onClose
}: {
  member: TeamMember | null;
  isRTL: boolean;
  onClose: () => void;
}) => {
  if (!member) return null;

  return (
    <Dialog open={!!member} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold gradient-text">
            {member.name[isRTL ? 'ar' : 'en']}
          </DialogTitle>
          <DialogDescription className="text-lg text-primary font-semibold">
            {member.role[isRTL ? 'ar' : 'en']}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image & Basic Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="aspect-square rounded-2xl overflow-hidden bg-muted">
              <img
                src={member.image}
                alt={member.name[isRTL ? 'ar' : 'en']}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-4">
              {/* Bio */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  {isRTL ? 'نبذة' : 'About'}
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  {member.bio[isRTL ? 'ar' : 'en']}
                </p>
              </div>

              {/* Contact */}
              <div className="space-y-2">
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    {member.email}
                  </a>
                )}
                {member.phone && (
                  <a
                    href={`tel:${member.phone}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {member.phone}
                  </a>
                )}
              </div>

              {/* Social Media */}
              {member.social && Object.keys(member.social).length > 0 && (
                <div className="flex gap-2 pt-4 border-t">
                  {member.social.facebook && (
                    <a
                      href={member.social.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {member.social.instagram && (
                    <a
                      href={member.social.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  )}
                  {member.social.twitter && (
                    <a
                      href={member.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    >
                      <Twitter className="h-5 w-5" />
                    </a>
                  )}
                  {member.social.linkedin && (
                    <a
                      href={member.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats (for coaches) */}
          {member.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-2xl bg-primary/5 border border-primary/20">
              {member.stats.playersCoached && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {member.stats.playersCoached}+
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? 'لاعب مُدرّب' : 'Players Coached'}
                  </div>
                </div>
              )}
              {member.stats.yearsExperience && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {member.stats.yearsExperience}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? 'سنوات خبرة' : 'Years Experience'}
                  </div>
                </div>
              )}
              {member.stats.championships && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {member.stats.championships}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? 'بطولات' : 'Championships'}
                  </div>
                </div>
              )}
              {member.stats.successRate && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {member.stats.successRate}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? 'نسبة النجاح' : 'Success Rate'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Education */}
          {member.education && (
            <div className="p-4 rounded-xl bg-card border">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                {isRTL ? 'التعليم' : 'Education'}
              </h4>
              <p className="text-muted-foreground">
                {member.education[isRTL ? 'ar' : 'en']}
              </p>
            </div>
          )}

          {/* Certifications */}
          {member.certifications && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                {isRTL ? 'الشهادات' : 'Certifications'}
              </h4>
              <div className="grid md:grid-cols-2 gap-2">
                {member.certifications[isRTL ? 'ar' : 'en'].map((cert, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-3 rounded-lg bg-surface-1"
                  >
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-sm">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Achievements */}
          {member.achievements && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                {isRTL ? 'الإنجازات' : 'Achievements'}
              </h4>
              <div className="space-y-2">
                {member.achievements[isRTL ? 'ar' : 'en'].map((achievement, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-surface-1"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 flex-shrink-0 mt-0.5">
                      <span className="text-primary font-bold text-xs">{idx + 1}</span>
                    </div>
                    <span className="text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quote */}
          {member.quote && (
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
              <Quote className="h-8 w-8 text-primary mb-3 opacity-50" />
              <p className="text-lg italic leading-relaxed">
                "{member.quote[isRTL ? 'ar' : 'en']}"
              </p>
            </div>
          )}

          {/* Languages */}
          {member.languages && member.languages.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Globe className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold">
                {isRTL ? 'اللغات:' : 'Languages:'}
              </span>
              {member.languages.map((lang, idx) => (
                <Badge key={idx} variant="secondary">
                  {lang}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Team;
